import './styles.css';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './_redux/store';

import { LoadingHandler } from './components/parts/Parts';
import { Core } from './components/core/Core';
import { Footer, Navigation } from './components/navigation/Navigation';
import { CaseStudies } from './components/case-studies/CaseStudies';
import { Dashboard } from './components/admin/Dashboard';
import CaseStudyDetail from './components/case-studies/CaseStudyDetail';
import { AuthAccess } from './components/admin/AuthAccess';
import { PageNotFound } from './components/navigation/ErrorHandling';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { fetchCaseStudies, fetchCaseStudyIdAltRange } from './_redux/actions/CaseStudyActions';

// Suppress React Router's default scroll-to-top behavior
function useScrollRestoration() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Delay scrolling to the top until animations complete
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 1000); // Match this duration to your fade-out animation (1 second)

    return () => clearTimeout(timer);
  }, [pathname]);
}

function AppContent() {

  const [isTransitioning, setIsTransitioning] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();

  useScrollRestoration();

  useEffect(() => {
    dispatch(fetchCaseStudies());
    dispatch(fetchCaseStudyIdAltRange());
  }, [dispatch]);

  return (
    <>
      <AnimatePresence
        mode="wait"
        onExitComplete={() => {
          setIsTransitioning(false); // Ensure clean transition
        }}
      >
        {isTransitioning ? (
          <motion.div
            key="loading"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }} // 1 second fade-out
          >
            <LoadingHandler />
          </motion.div>
        ) : (
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }} // 1 second fade-in
          >
            <Navigation />
            <Routes location={location}>
              <Route path="/" element={<Core />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth-access" element={<AuthAccess />} />
              <Route path="/*" element={<PageNotFound />} />
            </Routes>
            <Footer />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
}

export default App;
